import { applyMiddleware, compose, createStore } from "redux";
import { composeWithDevTools } from "redux-devtools-extension";
import produce, { enableAllPlugins, setUseProxies } from "immer";
import createSagaMiddleware from "redux-saga";
import createSagaMonitor from "@clarketm/saga-monitor";
import { all, call, delay, spawn } from "redux-saga/effects";
import getDeep from "lodash.get";
import setDeep from "lodash.set";
import { makeDuckContextFn, makeSagaContextFn } from "./filesbytype";

import { ONE_SECOND, TEN } from "./theme/constants/constants";

enableAllPlugins();

const isIE11 = /Trident.*rv:11\.0/.test(navigator.userAgent);
if (isIE11){
    setUseProxies(false);
}

const actionTypes = Symbol("actionTypes");
const reducerDefaultId = Symbol("reducerDefaultId");
const isContextBasedReducer = Symbol("isContextBasedReducer");
const reducers = {};
const sagas = [];
const isDevelopment = process.env.NODE_ENV === "development";
const debugMode = window?.location?.search?.includes("debug");

const isString = stringValue => (typeof stringValue) === "string";

const combineSagas = sagasToCombine => {
    return function* rootSaga() {
        yield all(sagasToCombine.map(saga =>
            spawn(function* rootSagaWrapper () {
                while (true) {
                    try {
                        yield call(saga);
                        break;
                    } catch (e) {
                        console.log(`Error in ${saga.displayName}`, e);
                        // if the saga crashes right after starting, don't just run it again immidiately
                        yield delay(ONE_SECOND * TEN);
                    }
                }
            }))
        );
    };
};


const loggerMiddleware = () => {
    const isDebugMode =  false; // TODO: implement debug mode. Use new flag in app.config or reuse logLevels

    return next => action => {
        if (!isDebugMode) {
            return next(action); 
        }
        const t0 = performance.now();
        const returnValue = next(action);
        const t1 = performance.now();
        const timeTaken = t1 - t0;
        if (timeTaken > 100) {
            console.warn(`WARNING: Action ${action.type} took ${t1 - t0} milliseconds!`, action);
        }
        return returnValue;
    };
};

const createReducer = allReducers => {
    const reducerEntries = Object.entries(allReducers);
    return (state = {}, action) => {
        const { meta: { scopeID, mdContext } = {}, ignoreUnhandled } = action || { meta: {} };
        let handled = false;
        const finalState = reducerEntries.reduce((currentState, [ key, reducer ]) => {
            if(!reducer[actionTypes] || reducer[actionTypes].length === 0) {
                console.warn(`Reducer ${key} doesn't handle any action! Check that you export all actionTypes!`);
                return currentState;
            }
            const reducerHandlesActionType = reducer[actionTypes].includes(action.type);
            if(!reducerHandlesActionType) {
                return currentState;
            }
            if(handled) {
                console.warn(
                    `Action type ${action.type} was handled multiple times! This will cause the default states to overwrite each other! Check ${key}!`
                );
            }
            handled = true;
            const resolvedScopeId = (reducer[isContextBasedReducer] && mdContext?.[reducer[isContextBasedReducer]])
                // eslint-disable-next-line no-mixed-operators
                ? `${reducer[isContextBasedReducer]}.${mdContext[reducer[isContextBasedReducer]]}` 
                // eslint-disable-next-line no-mixed-operators
                : (scopeID || reducer[reducerDefaultId] || "global");
            const produceState = draftState => {
                const stateSlice = getDeep(draftState, `${resolvedScopeId}`);
                const updatedSubState = reducer(stateSlice, action, { __context: state, __scopeID: resolvedScopeId });
                setDeep(draftState, `${resolvedScopeId}`, updatedSubState);
                return draftState;
            };
            return produce(currentState, produceState);
        }, state);
        if(!handled && !ignoreUnhandled && !action.type.startsWith("@@")) {
            console.warn(`Action type ${action.type} wasn't handled by any reducer! Check that you export this action type from the duck!`);
        }
        return finalState;
    };
};

const sagaMonitorConfig = {
    level: isDevelopment ? "log" : "error",
    effectTrigger: false,
    effectResolve: false,
    actionDispatch: false
};
// eslint-disable-next-line max-statements
function configureStore(preloadedState, { sagaContext: providedSagaContext }) {
    const sagaConfig = {
        onError: (error, { sagaStack }) => {
            console.error(error, { sagaStack });
        },
    };
    if (process.env.JEST_WORKER_ID === undefined) { // we're not testing
        // sagaConfig.sagaMonitor = createSagaMonitor(sagaMonitorConfig);
    }
    const sagaMiddleware = createSagaMiddleware(sagaConfig);

    const middlewares = [ loggerMiddleware, sagaMiddleware ];
    const middlewareEnhancer = applyMiddleware(...middlewares);

    const enhancers = [middlewareEnhancer];
    let composedEnhancers = compose(middlewareEnhancer);
    if (isDevelopment || debugMode) {
        composedEnhancers = composeWithDevTools({ serialize: true })(middlewareEnhancer);
        //  redux-immutable-state-invariant
        //  createSerializableStateInvariantMiddleware
    }

    const duckContextFn = makeDuckContextFn();
        
    // default export is the reducer
    // todo: load as needed only!
    let dynamicReducers = Object.assign(
        {},
        ...duckContextFn.keys()
            .map(key => [ key, duckContextFn(key) ])
            .map(([ key, exporto ]) => {
                exporto.default[actionTypes] = Object.values(exporto).filter(isString);
                exporto.default[reducerDefaultId] = exporto.defaultScopeId;
                exporto.default[isContextBasedReducer] = exporto.contextKey || false;
                const storeKey = exporto.default[isContextBasedReducer]
                    ? key.match(/\/([a-zA-Z0-9]+)\/([a-zA-Z0-9]+)[/]*([a-zA-Z0-9]*)[/]*([a-zA-Z0-9]*)\.duck\.js/).slice(1, 5)
                        .filter(i => i !== "dataTypes")
                        .join(".")
                    : key.match(/\/([a-zA-Z0-9]+)\/([a-zA-Z0-9]+)\.duck\.js/).slice(1, 3)
                        .join(".");
                return ({
                    [storeKey]: exporto.default
                    // todo: assosiate the exported actions from this reducer with this reducer, to identify which action is handled by which reducer
                });
            })
    );

    const sagaContextFn = makeSagaContextFn();
    // default export is the root-saga creator
    let dynamicSagas = sagaContextFn.keys().map(key => [ key, sagaContextFn(key) ])
        .map(([ key, exporto ]) => {
            const r = exporto.default();
            r.displayName = key;
            return r;
        });

    const store = createStore(createReducer({ ...reducers, ...dynamicReducers }), preloadedState, composedEnhancers);
    let sagaTask = sagaMiddleware.run(combineSagas([ ...sagas, ...dynamicSagas ]), {
        context: providedSagaContext
    });

    if (process.env.NODE_ENV !== "production" && module.hot) {
        module.hot.accept(
            duckContextFn.id,
            () => {
                dynamicReducers = Object.assign(
                    {},
                    ...duckContextFn.keys()
                        .map(key => [ key, duckContextFn(key) ])
                        .map(([ key, exporto ]) => {  
                            exporto.default[actionTypes] = Object.values(exporto).filter(isString);
                            exporto.default[reducerDefaultId] = exporto.defaultScopeId;
                            exporto.default[isContextBasedReducer] = exporto.contextKey || false;
                            const storeKey = exporto.default[isContextBasedReducer]
                                ? key.match(/\/([a-zA-Z0-9]+)\/([a-zA-Z0-9]+)[/]*([a-zA-Z0-9]*)[/]*([a-zA-Z0-9]*)\.duck\.js/).slice(1, 5)
                                    .filter(i => i !== "dataTypes")
                                    .join(".")
                                : key.match(/\/([a-zA-Z0-9]+)\/([a-zA-Z0-9]+)\.duck\.js/).slice(1, 3)
                                    .join(".");
                            return ({
                                [storeKey]: exporto.default
                            });
                        })
                );
                store.replaceReducer(createReducer({ ...reducers, ...dynamicReducers }));
            }
        ); 
        module.hot.accept(sagaContextFn.id, async () => {
            dynamicSagas = sagaContextFn.keys().map(key => [ key, sagaContextFn(key) ])
                .map(([ key, exporto ]) => {
                    const r = exporto.default();
                    r.displayName = key;
                    return r;
                });
            sagaTask.cancel();
            await sagaTask.toPromise();
            // eslint-disable-next-line require-atomic-updates
            sagaTask = sagaMiddleware.run(combineSagas([ ...sagas, ...dynamicSagas ]));
        });
    }



    store.asyncReducers = {};
    // Create an inject reducer function
    // This function adds the async reducer, and creates a new combined reducer
    store.injectReducer = (key, asyncReducer) => {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer({ ...reducers, ...dynamicReducers, ...store.asyncReducers }));
    };
    store.injectReducer.removeReducer = key => {
        delete store.asyncReducers[key];
        store.replaceReducer(createReducer({ ...reducers, ...dynamicReducers, ...store.asyncReducers }));
    };
    store.runSaga = sagaMiddleware.run;

    store.dispatch({ type: "INIT_MD", ignoreUnhandled: true });

    return store;
}

const g = (window ?? global ?? self ?? this ?? {});
export const store = configureStore({}, { sagaContext: g.sagaContext || {} });
export const makeStore = initialState => configureStore(initialState, { sagaContext: g.sagaContext || {} });
